import React from 'react';
import { SelectProps, Select as TUISelect } from '@apia/theme';
import { getVariant } from '@apia/theme';
import { useField } from '../hooks/useField';
import { useValidationClass } from '../hooks/useValidationClass';
import { TDefaultFieldType } from './types';
import { FieldWrapper } from './FieldWrapper';
import { TId } from '@apia/util';

export type TOption = { value: TId; label: string };

export type TSelectProps = Omit<SelectProps, 'defaultValue'>;

export type TSelect = TSelectProps &
  TDefaultFieldType & {
    options: TOption[];
  };

export const Select = React.forwardRef<HTMLSelectElement, TSelect>(
  (
    {
      avoidSemicolon,
      className,
      formName,
      label,
      onChange: outerOnChange,
      options,
      /**
       * useField
       */
      avoidRegisterIfExists,
      initialValue,
      name,
      removeOnUnmount,
      submitValueParser,
      validationClass,
      validationFunction,
      validationRules,
      value,
      ...props
    },
    ref,
  ) => {
    const { actualValidationFunction, actualValidationRules } =
      useValidationClass({
        formName,
        name,
        validationClass,
        validationFunction,
        validationRules,
      });

    const { updateValidations, onChange, isTouched, ...inputProps } =
      useField<string>({
        avoidRegisterIfExists,
        initialValue: initialValue ?? (value as string) ?? options[0]?.value,
        name,
        removeOnUnmount,
        submitValueParser,
        validationFunction: actualValidationFunction,
        validationRules: actualValidationRules,
      });

    return (
      <FieldWrapper
        name={name}
        label={label}
        className={`select ${className ?? ''}`}
        avoidSemicolon={avoidSemicolon}
      >
        <TUISelect
          {...getVariant('forms.select')}
          {...props}
          {...inputProps}
          onChange={React.useCallback(
            (ev: React.ChangeEvent<HTMLSelectElement>) => {
              onChange(ev);
              if (outerOnChange) {
                outerOnChange(ev);
              }
            },
            [onChange, outerOnChange],
          )}
          aria-label={props['aria-label'] ?? label}
          ref={ref}
          value={inputProps.value ?? ''}
        >
          {options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
          {props.children}
        </TUISelect>
      </FieldWrapper>
    );
  },
);

Select.displayName = 'Select';
